import { absBig, BIG_ONE, BIG_TWO, BIG_ZERO } from "@/lib/tool-kit/math/bigint";
import { toSuperscript } from "@/lib/tool-kit/math/primes";
import {
  compareR,
  divR,
  equalsR,
  fromBigInt,
  intR,
  isZeroR,
  makeRational,
  mulR,
  RATIONAL_ZERO,
  type Rational,
  signR,
  subR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * Geometric sequences in exact rational arithmetic.
 *
 * aₙ = a₁·rⁿ⁻¹, Sₙ = a₁(1 − rⁿ) ÷ (1 − r) for r ≠ 1 (and n·a₁ for r = 1), and the
 * infinite sum a₁ ÷ (1 − r) exists only for |r| < 1. Term numbers stop at `MAX_N` so that
 * rⁿ stays a number the browser can write down.
 */

export const MAX_N = 2000;

const ONE = intR(1);

/** r^k for a whole number k ≥ 0, by repeated squaring (0⁰ is 1). */
export function rationalPow(base: Rational, exponent: number): Rational {
  let result = ONE;
  let square = base;
  let remaining = exponent;
  while (remaining > 0) {
    if (remaining % 2 === 1) {
      result = mulR(result, square);
    }
    remaining = Math.floor(remaining / 2);
    if (remaining > 0) {
      square = mulR(square, square);
    }
  }
  return result;
}

export function nthTerm(a1: Rational, r: Rational, n: number): Rational {
  return mulR(a1, rationalPow(r, n - 1));
}

/** The sum of the first n terms. */
export function partialSum(a1: Rational, r: Rational, n: number): Rational {
  if (equalsR(r, ONE)) {
    return mulR(a1, fromBigInt(BigInt(n)));
  }
  return divR(mulR(a1, subR(ONE, rationalPow(r, n))), subR(ONE, r));
}

/** |r| < 1 → the sum of every term; otherwise null (zero if a₁ is 0). */
export function infiniteSum(a1: Rational, r: Rational): Rational | null {
  if (isZeroR(a1)) {
    return RATIONAL_ZERO;
  }
  const size = compareR({ num: absBig(r.num), den: r.den }, ONE);
  return size < 0 ? divR(a1, subR(ONE, r)) : null;
}

export function firstTerms(
  a1: Rational,
  r: Rational,
  count: number
): Rational[] {
  const terms: Rational[] = [];
  let current = a1;
  for (let i = 0; i < count; i += 1) {
    terms.push(current);
    current = mulR(current, r);
  }
  return terms;
}

/* ------------------------------------------------------------ roots */

/** ⌊n^(1/k)⌋ for n ≥ 0 by binary search. */
export function integerRoot(n: bigint, k: number): bigint {
  if (n < BIG_TWO || k === 1) {
    return n;
  }
  const kBig = BigInt(k);
  let low = BIG_ONE;
  let high = BIG_TWO ** BigInt(Math.ceil(n.toString(2).length / k) + 1);
  while (low < high) {
    const mid = (low + high + BIG_ONE) / BIG_TWO;
    if (mid ** kBig <= n) {
      low = mid;
    } else {
      high = mid - BIG_ONE;
    }
  }
  return low;
}

function exactRoot(n: bigint, k: number): bigint | null {
  const root = integerRoot(n, k);
  return root ** BigInt(k) === n ? root : null;
}

/* ------------------------------------------------------- two terms */

export interface RatioFit {
  r: Rational;
  a1: Rational;
}

export type TwoTermResult =
  | { kind: "ok"; fits: RatioFit[] }
  | { kind: "irrational"; r: number; a1: number }
  | { kind: "none" | "same-position" | "all-zero" | "too-large" };

/** Every real ratio r with aₚ = x and a_q = y, for p ≠ q and nonzero x and y. */
export function fitTwoTerms(
  p: number,
  x: Rational,
  q: number,
  y: Rational
): TwoTermResult {
  if (p === q) {
    return { kind: "same-position" };
  }
  if (isZeroR(x) && isZeroR(y)) {
    return { kind: "all-zero" };
  }
  if (isZeroR(x) || isZeroR(y)) {
    return { kind: "none" };
  }
  const [near, nearValue, far, farValue] = p < q ? [p, x, q, y] : [q, y, p, x];
  const gap = far - near;
  const quotient = divR(farValue, nearValue);
  const negative = signR(quotient) < 0;
  if (negative && gap % 2 === 0) {
    return { kind: "none" };
  }
  const num = exactRoot(absBig(quotient.num), gap);
  const den = exactRoot(quotient.den, gap);
  if (num === null || den === null) {
    const magnitude = Math.abs(toNumberR(quotient)) ** (1 / gap);
    if (!Number.isFinite(magnitude)) {
      return { kind: "too-large" };
    }
    const r = negative ? -magnitude : magnitude;
    return {
      kind: "irrational",
      r,
      a1: toNumberR(nearValue) / r ** (near - 1),
    };
  }
  const root = makeRational(num, den);
  const roots =
    negative || gap % 2 === 1
      ? [negateIf(root, negative)]
      : [root, negateIf(root, true)];
  return {
    kind: "ok",
    fits: roots.map((r) => ({
      r,
      a1: divR(nearValue, rationalPow(r, near - 1)),
    })),
  };
}

function negateIf(value: Rational, negate: boolean): Rational {
  return negate ? { num: -value.num, den: value.den } : value;
}

/* -------------------------------------------------------- position */

export type PositionResult =
  | { kind: "term"; n: number }
  | { kind: "not-a-term" | "every-term" };

/** Which term number holds `target`, searching up to `MAX_N` terms. */
export function positionOf(
  a1: Rational,
  r: Rational,
  target: Rational
): PositionResult {
  if (equalsR(a1, target)) {
    return equalsR(r, ONE) || isZeroR(a1)
      ? { kind: "every-term" }
      : { kind: "term", n: 1 };
  }
  if (isZeroR(a1) || equalsR(r, ONE)) {
    return { kind: "not-a-term" };
  }
  const magnitude = (v: Rational) =>
    compareR({ num: absBig(v.num), den: v.den }, ONE);
  const growing = magnitude(r) > 0;
  const wanted = { num: absBig(target.num), den: target.den };
  let term = a1;
  for (let n = 1; n <= MAX_N; n += 1) {
    if (equalsR(term, target)) {
      return { kind: "term", n };
    }
    const size = { num: absBig(term.num), den: term.den };
    const passed = growing
      ? compareR(size, wanted) > 0
      : compareR(size, wanted) < 0;
    if (passed && magnitude(r) !== 0) {
      break;
    }
    term = mulR(term, r);
  }
  return { kind: "not-a-term" };
}

/* ---------------------------------------------------------- display */

/** A rational as 1.234567890×10⁴⁵ — for values too long to write out. */
export function scientific(value: Rational, significant = 10): string {
  if (isZeroR(value)) {
    return "0";
  }
  const negative = value.num < BIG_ZERO;
  const num = absBig(value.num);
  const estimate = num.toString().length - value.den.toString().length;
  const shift = significant + 2 - estimate;
  const scaled =
    shift >= 0
      ? (num * BigInt(10) ** BigInt(shift)) / value.den
      : num / (value.den * BigInt(10) ** BigInt(-shift));
  const digits = scaled.toString();
  const exponent = digits.length - 1 - shift;
  const mantissa = `${digits[0]}.${digits.slice(1, significant)}`;
  return `${negative ? "−" : ""}${mantissa}×10${toSuperscript(exponent)}`;
}

/** Digits before the decimal point, plus the denominator's, for deciding how to show it. */
export function digitCount(value: Rational): number {
  return Math.max(
    absBig(value.num).toString().length,
    value.den.toString().length
  );
}
